<template>
  <div class="vx-card p-6">
    <base-ag-grid-header
      :rows-selected-count="rowsSelectedCountWithAllSelection"
      :filters-count="filtersAppliedCount"
      :filters-match.sync="filtersMatch"
      @reset-selection="setSelectedRowsNone()"
      @reset-filters="resetFilters()">
      <template v-slot:header-left>
        <items-per-page
          :count="count"
          :currentPage="currentPage"
          :paginationPageSize="paginationPageSize"
          @changed-page-size="paginationSetPageSize">
        </items-per-page>

        <list-action-drop-down
          v-if="isAnyRowsSelected"
          :options="defaultMultipleActionOptions"
          class="ml-3"
          @export="exportData()"
          @delete="confirmDeleteRecords()">
        </list-action-drop-down>
      </template>

      <template v-slot:header-right>
        <page-header-actions
          class="hidden md:flex"
          :actions="headerActions"
          @action="onHeaderAction"
        />
      </template>
    </base-ag-grid-header>

    <selection-alert-info
        v-model="selectionPageAlert"
        class="mb-5"
        :entity="entity"
        :count="count"
        :rows-selected-count="rowsSelectedCount"
        @select-all="setSelectedRowsAll()"></selection-alert-info>

    <export-alert-info
      :entity="$tc(`$Entities.${entity}`, 2)"
      :exporting.sync="exportingList"
      :exported.sync="exportedList"
      :fileUrl="urlFileExported"
      :send-email="exportSendEmail"/>

    <ag-grid-vue
      :key="agGridKey"
      ref="agGridTable"
      :domLayout="domLayout"
      :style="gridStyle"
      :components="components"
      :frameworkComponents="frameworkComponents"
      :gridOptions="gridOptions"
      class="ag-theme-material w-100 ag-grid-table"
      :columnDefs="columnDefs"
      :defaultColDef="defaultColDef"
      :column-types="columnTypes"
      :getRowNodeId="getRowNodeId"
      :autoParamsRefresh="true"
      rowSelection="multiple"
      :animateRows="true"
      :suppressRowClickSelection="true"
      rowModelType="infinite"
      :pagination="hasPagination"
      :paginationPageSize="paginationPageSize"
      :suppressPaginationPanel="suppressPaginationPanel"
      :overlayLoadingTemplate="overlayLoadingTemplate"
      :noRowsOverlayComponent="noRowsOverlayComponent"
      :noRowsOverlayComponentParams="noRowsOverlayComponentParams"
      :maxConcurrentDatasourceRequests="maxConcurrentDatasourceRequests"
      :cacheBlockSize="cacheBlockSize"
      :maxBlocksInCache="maxBlocksInCache"
      @selection-changed="onSelectionChanged"
      @grid-ready="onGridReady"
      @model-updated="onModelUpdate"
      @grid-size-changed="onGridSizeChanged"
      @first-data-rendered="onFirstDataRendered"
      @filter-changed="onFilterChanged"
      @pagination-changed="onPaginationChanged">
    </ag-grid-vue>

    <!-- Mobile Sticky Bottom Bar -->
    <div
      class="sticky-bottom-bar-icons md:hidden"
      :style="{ gridTemplateColumns: `repeat(${(isAnyRowsSelected ? 1 : 0) + 1}, 1fr)` }"
    >
      <vs-dropdown
        v-if="isAnyRowsSelected"
        vs-custom-content
        vs-trigger-click
        class="mobile-bar-btn mobile-bar-btn-simple"
      >
        <div class="mobile-bar-btn-content">
          <i class="feather icon-more-horizontal mobile-bar-icon"></i>
          <span class="mobile-btn-label">Actions</span>
        </div>
        <vs-dropdown-menu class="mobile-actions-dropdown gp-sticky-dropdown-menu">
          <ul class="mobile-actions-list">
            <li
              v-for="(opt, idx) in defaultMultipleActionOptions"
              :key="`mba-${idx}`"
              class="mobile-actions-item"
              @click="onMobileBulkAction(opt.event)"
            >
              <span class="flex items-center">
                <feather-icon :icon="opt.icon" svgClasses="h-4 w-4" class="mr-2" />
                <span>{{ opt.label }}</span>
              </span>
            </li>
          </ul>
        </vs-dropdown-menu>
      </vs-dropdown>

      <button
        class="mobile-bar-btn mobile-bar-btn-simple mobile-bar-btn-refresh"
        type="button"
        @click="resetDataSourceAndSelection()"
      >
        <i class="feather icon-refresh-cw mobile-bar-icon"></i>
        <span class="mobile-btn-label">Refresh</span>
      </button>
    </div>

    <vs-popup
      :title="exportModalTitle"
      :active.sync="activeModalToExport">
      <transition name="zoom-fade">
        <export-json-to-excel
          v-if="showExportComponent"
          :columns="getColumnsToExport"
          :data="rowsSelected"
          @close="activeModalToExport=false"
          @export="onExport">
        </export-json-to-excel>
      </transition>
    </vs-popup>
  </div>
</template>

<script>
import '@/assets/scss/vuexy/extraComponents/agGridStyleOverride.scss';
import { mapActions } from 'vuex';

// Mixins
import commonListCreateOrEditWithAgGrid from '@/views/modules/_mixins/commonListCreateOrEditWithAgGrid';

// Cell Renderer
import CellRendererActions from '@/views/modules/inbound-sms/InboundSMSListCellRendererActions.vue';
import CellRendererLargeText from '@/views/modules/_components/cell-renderer/CellRendererLargeTextModal.vue';

// Custom Components
import BaseAgGridHeader from '@/views/modules/_components/BaseAgGridHeader.vue';
import ItemsPerPage from '@/views/modules/_components/ItemsPerPage.vue';
import ExportJsonToExcel from '@/views/modules/_components/ExportJsonToExcel.vue';
import ListActionDropDown from '@/views/modules/_components/ListActionDropDown.vue';
import AgGridSelectionHeader from '@/views/modules/_components/AgGridSelectionHeader';
import ExportAlertInfo from '@/views/modules/_components/ExportAlertInfo.vue';
import CustomNoRowsOverlay from '@/views/modules/_components/customAgGridNoRowsOverlay';
import SelectionAlertInfo from '@/views/modules/SelectionAlertInfo.vue';
import PageHeaderActions from '@/views/modules/_components/PageHeaderActions.vue';

/**
 * Component to list and SMS inbounds
 *
 * @module src/views/modules/inbound-sms/InboundSMSList
 * @author Dilan Useche <dilan8810@gmail.com>
 *
 */
export default {
  name: 'InboundSMSList',
  i18n: {
    messages: {
      en: {
        ThereAreNoInboundSMS: 'No inbound SMS to display',
        $NoRowsOverlay: {
          Title: 'No inbound SMS messages to display',
          Text: 'There are no reply messages to view right now. Create an SMS campaign to start receiving replies from your contacts.',
          Button: 'Create new SMS campaign',
        },
      },
    },
  },
  components: {
    BaseAgGridHeader,
    ExportJsonToExcel,
    ItemsPerPage,
    ListActionDropDown,
    ExportAlertInfo,
    PageHeaderActions,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererActions,
    // eslint-disable-next-line vue/no-unused-components
    CellRendererLargeText,
    SelectionAlertInfo,
  },
  mixins: [
    commonListCreateOrEditWithAgGrid,
  ],
  props: {
    actionsMinWidth: {
      type: Number,
      required: false,
      default: 30,
    },
  },
  data() {
    return {
      entity: this.$enums.Entity.SMS_INBOUND,
      cloneDisabled: true,
      viewDisabled: true,
      editDisabled: true,
      cellRenderer: false,
      exportCollectionFunction: this.exportSmsInbounds,
      deleteRecordFunction: this.removeInboundMessage,
      deleteRecordsFunction: this.removeInboundMessages,
      preferFitColumns: true,
      noRowsMessage: this.$t('ThereAreNoInboundSMS'),
      columnDefs: [
        {
          colId: 'from',
          headerName: this.$t('$General.From'),
          field: 'from',
          filter: true,
          minWidth: 250,
          maxWidth: 350,
          valueFormatter: (params) => (params.value && params.data && params.data.contact
            ? `${params.data.contact.name}(${params.value})`
            : params.value),
          checkboxSelection: true,
          headerComponentParams: { checkboxSelection: true },
        },
        {
          colId: 'to',
          headerName: this.$t('$General.To'),
          field: 'to',
          filter: true,
          minWidth: 250,
          maxWidth: 350,
        },
        {
          colId: 'campaign',
          headerName: this.$tc('$General.Campaign'),
          field: 'campaign',
          filter: false,
          minWidth: 250,
          maxWidth: 350,
          valueFormatter: (params) => (params.value && params.value.name
            ? params.value.name
            : ''),
        },
        {
          colId: 'message',
          headerName: this.$tc('$General.Message'),
          field: 'message',
          filter: true,
          minWidth: 250,
          maxWidth: 1906,
          cellRendererFramework: 'CellRendererLargeText',
        },
      ],
      components: {
        CellRendererActions,
        CellRendererLargeText,
      },
      frameworkComponents: {
        agColumnHeader: AgGridSelectionHeader,
        customNoRowsOverlay: CustomNoRowsOverlay,
      },
    };
  },
  computed: {
    headerActions() {
      return [
        {
          id: 'refresh',
          tooltip: 'Refresh',
          kind: 'icon',
          icon: 'icon-refresh-cw',
        },
      ];
    },
  },
  beforeMount() {
    const $this = this;
    this.noRowsOverlayComponent = 'customNoRowsOverlay';
    this.noRowsOverlayComponentParams = {
      title: this.$t('$NoRowsOverlay.Title'),
      text: this.$t('$NoRowsOverlay.Text'),
      buttonText: this.$t('$NoRowsOverlay.Button'),
      async actionButtonClicked() {
        await $this.$router.push({
          name: 'sms-campaigns-create',
        });
      },
    };
  },
  methods: {
    onHeaderAction({ id }) {
      switch (id) {
        case 'refresh':
          this.resetDataSourceAndSelection();
          break;
        default:
          break;
      }
    },
    onMobileBulkAction(event) {
      switch (event) {
        case 'export':
          this.exportData();
          break;
        case 'delete':
          this.confirmDeleteRecords();
          break;
        default:
          break;
      }
    },
    ...mapActions({
      fetchAllInboundsSms: 'inboundSms/fetchAllInboundsSms',
      exportSmsInbounds: 'inboundSms/exportFile',
      removeInboundMessage: 'inboundSms/removeInboundMessage',
      removeInboundMessages: 'inboundSms/removeInboundMessages',
    }),
    async fetchAgGridData(params) {
      return this.fetchAllInboundsSms({
        ...params,
        populate: [
          { path: 'contact', select: 'id firstName lastName name' },
          { path: 'campaign', select: 'id name' },
        ],
      });
    },
  },
};
</script>

<style scoped lang="scss">
// Mobile Sticky Bottom Bar (icon buttons)
.sticky-bottom-bar-icons {
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top: 2px solid #E5E7EB;
  padding: 8px 12px;
  display: grid;
  gap: 8px;
  box-shadow: 0 -4px 6px -1px rgba(0, 0, 0, 0.1);
  z-index: 100;
  margin: 0 -1.5rem -1.5rem; // match vx-card p-6

  @media (max-width: 767px) {
    position: -webkit-sticky;
    position: sticky;
  }
}

.mobile-bar-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px 6px;
  border: none;
  background: transparent;
  cursor: pointer;
  border-radius: 8px;
  transition: all 0.15s ease;
  color: #6B7280;
  font-family: inherit;
  outline: none;

  &:active {
    transform: scale(0.95);
    background: rgba(139, 92, 246, 0.1);
  }
}

.mobile-bar-btn-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.mobile-bar-icon {
  font-size: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 6px auto;
  width: 22px;
  height: 22px;
}

.mobile-btn-label {
  font-size: 11px;
  font-weight: 500;
  text-align: center;
  line-height: 1;
}

.mobile-bar-btn-simple {
  @media (hover: hover) {
    &:hover {
      background: rgba(139, 92, 246, 0.05);
    }
  }

  &:active {
    background: rgba(139, 92, 246, 0.1) !important;
  }
}

.mobile-bar-btn-refresh {
  @media (hover: hover) {
    &:hover {
      background: rgba(139, 92, 246, 0.05);
    }
  }

  &:active {
    background: rgba(139, 92, 246, 0.1) !important;
  }

  &:not(:active):not(:hover) {
    background: transparent !important;
  }
}

.mobile-actions-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.mobile-actions-item {
  padding: 10px 12px;
  cursor: pointer;

  &:hover {
    background: rgba(139, 92, 246, 0.06);
  }
}
</style>
